/**
 * Event domain types — shared between manager-web and guest-portal.
 *
 * Mirrors backend `internal/events/domain` (PR #110, commit d3d6246):
 *  - 7 closed-taxonomy categories (party / lecture / kids / sport / spa
 *    / general / excursion)
 *  - 4 lifecycle states (draft → published ↔ hidden → deleted)
 *  - Backend response shape from `internal/events/http_app/dto.eventDTO`.
 *
 * Branded `EventId` per FRONTEND_CODING_STANDARDS §3 (TS strict + branded IDs).
 */

import type { Brand, TenantId } from './index.js';

export type EventId = Brand<string, 'EventId'>;
export const EventId = (s: string): EventId => s as EventId;

/** Closed taxonomy — must match backend `domain.EventCategory`. */
export const EVENT_CATEGORIES = [
  'party',
  'lecture',
  'kids',
  'sport',
  'spa',
  'general',
  'excursion',
] as const;
export type EventCategory = (typeof EVENT_CATEGORIES)[number];

/** Lifecycle states — must match backend `domain.EventState`. */
export const EVENT_STATES = ['draft', 'published', 'hidden', 'deleted'] as const;
export type EventState = (typeof EVENT_STATES)[number];

/**
 * Event resource as returned by GET/POST/PATCH/POST publish/POST hide.
 * Field names match backend JSON tags in `eventDTO`.
 */
export interface Event {
  id: EventId;
  tenant_id: TenantId;
  title: string;
  description: string;
  category: EventCategory;
  state: EventState;
  starts_at: string;
  ends_at: string;
  created_at: string;
  created_by: string;
  updated_at: string;
  published_at?: string;
  published_by?: string;
  hide_reason?: string;
  /** Вместимость мероприятия (опц.). Нет поля → без ограничения мест. */
  capacity?: number;
  /** Обложка: RAW хранимая ссылка (MinIO object-key или http(s)-URL). Дизайн §12. */
  cover_url?: string;
  /** Короткоживущий presigned-GET для показа обложки (для object-key). */
  cover_preview_url?: string;
  /** Место проведения («терраса бассейна»). Дизайн §12. */
  location?: string;
  /** Ведущий («Юля Светлова»). Дизайн §12. */
  host?: string;
  /** Цена в копейках (опц.). Нет поля → «Бесплатно». Дизайн §12. */
  price_kopecks?: number;
  /** Валюта цены (ISO-4217, по умолчанию RUB). */
  price_currency?: string;
}

/** Body for POST /v1/events. */
export interface EventCreateInput {
  title: string;
  description: string;
  category: EventCategory;
  /** RFC3339 (e.g. `2026-06-05T18:00:00Z`). */
  starts_at: string;
  ends_at: string;
  /** Поля дизайна §12 (опц.). */
  cover_url?: string;
  location?: string;
  host?: string;
  capacity?: number | null;
  /** Цена в копейках (null/опущено = «Бесплатно»). */
  price_kopecks?: number | null;
  currency?: string;
}

/** Body for PATCH /v1/events/{id} — title / description / dates + §12 fields. */
export interface EventUpdateInput {
  title: string;
  description: string;
  starts_at: string;
  ends_at: string;
  cover_url?: string;
  location?: string;
  host?: string;
  capacity?: number | null;
  price_kopecks?: number | null;
  currency?: string;
}

/** Строка расписания дня (event_schedule, миграция 0066). «Время + текст». */
export interface ScheduleItem {
  id: string;
  time: string;
  text: string;
  sort_order: number;
}

/** Ответ GET /v1/events/schedule. */
export interface ScheduleListResponse {
  schedule: ScheduleItem[];
  total: number;
}

/** Body POST /v1/events/schedule. */
export interface ScheduleItemInput {
  time: string;
  text: string;
  sort_order?: number;
}

/** Body for POST /v1/events/{id}/hide. */
export interface EventHideInput {
  reason: string;
}

/** Query params for GET /v1/events. */
export interface EventListFilters {
  /** RFC3339 lower bound on starts_at. */
  from?: string;
  /** RFC3339 upper bound on starts_at. */
  to?: string;
  category?: EventCategory;
}

/** Response shape for GET /v1/events. */
export interface EventListResponse {
  events: Event[];
  total: number;
}

/**
 * Состояние гостевой записи на мероприятие (RSVP) — Волна 5 / A3 «Облака».
 * Совпадает с backend `domain.RSVPState`. Пустая строка → гость не записан.
 */
export const RSVP_STATES = ['going', 'waitlist', 'cancelled'] as const;
export type RsvpState = (typeof RSVP_STATES)[number];

/**
 * Ответ ручек RSVP: GET/POST/DELETE /v1/events/{id}/rsvp.
 * Поля совпадают с backend `http_app.rsvpStatusDTO`.
 */
export interface RsvpStatus {
  /** Состояние записи текущего гостя: going|waitlist|'' (не записан). */
  state: RsvpState | '';
  /** Активных записей «иду» по событию. */
  going: number;
  /** Гостей в листе ожидания. */
  waitlist: number;
}
